import { useCallback, useEffect, useRef, useState } from 'react';
import { useSIPProvider, CONNECT_STATUS, REGISTER_STATUS } from './sipProvider';

// The provider resolves only after REGISTERED and owns bounded transport recovery.
export default function usePhoneConnection() {
  const provider = useSIPProvider();
  const { sessionManager, connectStatus, registerStatus, connectAndRegister } = provider;
  const owner = useRef({ identity: '', starting: false, manager: null });
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  owner.current.manager = sessionManager;
  const ready = connectStatus === CONNECT_STATUS.CONNECTED && registerStatus === REGISTER_STATUS.REGISTERED;

  const connectPhone = useCallback(async ({ username, password }) => {
    if (!username || !password) { setError('Phone credentials are unavailable.'); return; }
    if (owner.current.starting || owner.current.identity === `${username}\u0000${password}`) return;
    owner.current.starting = true;
    try {
      owner.current.identity = `${username}\u0000${password}`;
      setError('');
      setAttempt(value => value + 1);
      await connectAndRegister({ username, password });
    } catch (connectionError) {
      owner.current.identity = '';
      setError(connectionError.message || 'Phone connection failed. Reload to retry.');
    } finally { owner.current.starting = false; }
  }, [connectAndRegister]);

  useEffect(() => {
    if (ready) { setError(''); return undefined; }
    if (!attempt) return undefined;
    const timer = setTimeout(() => setError(connectStatus === CONNECT_STATUS.CONNECTED
      ? 'Phone registration failed. Check the extension credentials and account status.'
      : 'Phone connection interrupted. Check your network, then reload to retry.'), 20000);
    return () => clearTimeout(timer);
  }, [attempt, ready, connectStatus]);

  return { ...provider, connectPhone, phoneReady: ready,
    phoneStatus: ready ? 'Phone ready' : error || (attempt ? 'Connecting phone' : 'Phone not connected') };
}
